import { useTranslation } from 'react-i18next';
import helpers from '../../../Utils/helpers';
import SortableTableHeader from '../../Fundamentals/Tables/SortableTableHeader';
import InvoiceRowActions from './InvoiceRowActions';
import type { TransactionRow } from '../../../Containers/Customer/CustomerTransactions.types';
import './transactions.css';

interface InvoicesTableProps {
  rows: TransactionRow[];
  sortKey: string | null;
  sortOrder: 'asc' | 'desc' | null;
  onSort: (key: string) => void;
  isInvoiceAdmin: boolean;
  onOpenDetails: (row: TransactionRow) => void;
  onSendCopy: (row: TransactionRow) => void;
  selectedRow: TransactionRow | null;
}

const InvoicesTable = ({
  rows,
  sortKey,
  sortOrder,
  onSort,
  isInvoiceAdmin,
  onOpenDetails,
  onSendCopy,
  selectedRow,
}: InvoicesTableProps) => {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return <p className="mb-0">{t('customertransactions.noEntriesFound')}</p>;
  }

  const renderHeader = (column: string, labelKey: string, className?: string) => (
    <SortableTableHeader
      sortColumn={column}
      currentSortKey={sortKey ?? undefined}
      sortOrder={sortOrder ?? undefined}
      onSort={onSort}
      className={className}
    >
      {t(labelKey)}
    </SortableTableHeader>
  );

  const renderInvoiceNumber = (row: TransactionRow) => {
    if (row.isUninvoiced) {
      return (
        <span className="d-flex align-items-center gap-8">
          <span className="transactions-pill transactions-pill--uninvoiced">
            {t('customertransactions.uninvoiced')}
          </span>
          {row.description}
        </span>
      );
    }
    return row.invoiceNumber;
  };

  return (
    <table className="table table-stripe table-active">
      <thead>
        <tr>
          {renderHeader('invoiceNumber', 'customertransactions.invoiceNumber')}
          {renderHeader('date', 'customertransactions.date')}
          {renderHeader('amount', 'customertransactions.amount', 'text-right')}
          {renderHeader('residual', 'customertransactions.residual', 'text-right')}
          {renderHeader('status', 'customertransactions.status')}
          {renderHeader('posted', 'customertransactions.posted')}
          <th className="text-center" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.id}
            className={row === selectedRow ? 'active' : undefined}
            onClick={() => onOpenDetails(row)}
          >
            <td>{renderInvoiceNumber(row)}</td>
            <td>{row.date !== undefined ? helpers.formatDate(row.date) : '—'}</td>
            <td className="text-right transactions-num">{helpers.currencyFormatter(row.amount)}</td>
            <td className={`text-right transactions-num${row.residual !== 0 ? ' text-red' : ''}`}>
              {helpers.currencyFormatter(row.residual)}
            </td>
            <td>{row.status !== undefined ? t(`invoiceStatuses.${row.status}`) : '—'}</td>
            <td>{row.posted !== undefined ? t(`invoicePaymentTypes.${row.posted}`) : '—'}</td>
            <td className="text-center">
              <InvoiceRowActions
                row={row}
                isInvoiceAdmin={isInvoiceAdmin}
                onViewDetails={onOpenDetails}
                onSendCopy={onSendCopy}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default InvoicesTable;
